/**
 * In-page confirmation dialog.
 *
 * Replaces `window.confirm()`, which is browser chrome rather than page UI: it
 * cannot be styled, blocks the event loop, and browsers may suppress it.
 * This wraps a Bootstrap modal in a promise, so a call site reads as
 * `if (!(await dialog.ask())) return;`.
 *
 * The module strips Bootstrap's `fade` class: with it, `show()` completes only
 * after the open transition, and Bootstrap's `hide()` returns early while that
 * transition runs — so Cancel, X and Escape are all swallowed for the first
 * ~300 ms the dialog is on screen. Without it both are synchronous.
 *
 * The accept button deliberately has no `data-bs-dismiss`. Bootstrap binds its
 * dismiss trigger on `document` in the capture phase, so it runs before any
 * listener on the dialog; closing there instead of here would make the click
 * indistinguishable from Cancel. Accepting is therefore handled here, and only
 * Cancel, Escape and the backdrop go through Bootstrap's dismiss path.
 *
 * The wording lives in the template; only the behaviour lives here.
 */
import Modal from 'bootstrap/js/dist/modal.js';

export class ConfirmDialog {
  #modal: Modal;
  #pending: Promise<boolean> | null = null;
  #settle: ((confirmed: boolean) => void) | null = null;
  #accepted = false;
  #previousFocus: HTMLElement | null = null;

  constructor(element: HTMLElement) {
    element.classList.remove('fade');
    this.#modal = new Modal(element);

    const accept = element.querySelector<HTMLButtonElement>('[data-confirm-accept]');
    if (!accept) {
      throw new Error('ConfirmDialog: element has no [data-confirm-accept] button');
    }

    accept.addEventListener('click', () => {
      this.#accepted = true;
      this.#modal.hide();
    });

    // Settling on `hidden` rather than on a click means every way of closing the
    // dialog resolves the promise exactly once, after the modal is really gone.
    element.addEventListener('hidden.bs.modal', () => {
      const settle = this.#settle;
      const accepted = this.#accepted;
      const previousFocus = this.#previousFocus;
      this.#settle = null;
      this.#pending = null;
      this.#accepted = false;
      this.#previousFocus = null;

      // Bootstrap only restores focus on the `data-bs-toggle` path, not for a
      // programmatic `show()`. On cancel the trigger is still valid, so return
      // there; after accepting it is usually disabled, so the caller decides.
      if (!accepted && previousFocus?.isConnected) {
        previousFocus.focus();
      }

      settle?.(accepted);
    });
  }

  /** Resolves `true` only if the user activated the confirming button. */
  ask(): Promise<boolean> {
    if (this.#pending) {
      return this.#pending;
    }

    this.#accepted = false;
    this.#previousFocus =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.#pending = new Promise<boolean>((resolve) => {
      this.#settle = resolve;
    });
    this.#modal.show();

    return this.#pending;
  }
}
